import React from 'react';

import { styled } from '@uhg-abyss/web/tools/styled';
import { Text } from '@uhg-abyss/web/ui/Text';
import { renderStars } from '../../../optumus-core/src/utilities/rating.utils';

export interface ProviderCardDetailsProps {
  firstName: string;
  lastName: string;
  education: string;
  specialty: string;
  rating: number;
  reviewCount: number;
}

const ProviderCardDetails: React.FC<ProviderCardDetailsProps> = ({
  firstName,
  lastName,
  education,
  specialty,
  rating,
  reviewCount,
}) => {
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  const displayName = education
    ? `${firstName} ${lastName}, ${education}`
    : `${firstName} ${lastName}`;
  const ratingLabel =
    rating > 0 ? `${rating.toFixed(1)} (${reviewCount} ratings)` : 'No ratings yet';

  return (
    <CardContainer>
      <CardHead>
        <Avatar>
          <AvatarText>{initials}</AvatarText>
        </Avatar>
        <HeadInfo>
          <ProviderName>{displayName}</ProviderName>
          <SpecialtyText>{specialty}</SpecialtyText>
          <RatingRow>
            <StarsText>{renderStars(rating)}</StarsText>
            <RatingText>{ratingLabel}</RatingText>
          </RatingRow>
        </HeadInfo>
      </CardHead>
    </CardContainer>
  );
};

const CardContainer = styled('div', {
  alignSelf: 'flex-end',
  width: '80%',
  borderWidth: '$core.border-width.sm',
  borderColor: '$core.color.neutral.20',
  borderRadius: '$core.border-radius.lg',
  overflow: 'hidden',
  backgroundColor: '$core.color.neutral.0',
  shadowColor: '$core.color.neutral.100',
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.08,
  shadowRadius: 14,
  elevation: 3,
});

const CardHead = styled('div', {
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '$core.spacing.150',
  paddingHorizontal: '$core.spacing.200',
  paddingVertical: '$core.spacing.200',
  backgroundColor: '$core.color.warm-white.10',
});

const Avatar = styled('div', {
  width: '$core.sizing.600',
  height: '$core.sizing.600',
  borderRadius: '$core.border-radius.full',
  backgroundColor: '$core.color.blue.10',
  borderWidth: '$core.border-width.sm',
  borderColor: '$core.color.blue.50',
  alignItems: 'center',
  justifyContent: 'center',
});

const HeadInfo = styled('div', {
  flex: 1,
  gap: '$core.spacing.50',
});

const AvatarText = styled(Text, {
  fontSize: '$core.font-size.p.80',
  color: '$core.color.brand.80',
  fontWeight: '$core.font-weight.heavy',
});

const ProviderName = styled(Text, {
  fontSize: '$core.font-size.p.80',
  color: '$core.color.brand.80',
  fontWeight: '$core.font-weight.xbold',
  letterSpacing: -0.4,
  lineHeight: 20,
});

const SpecialtyText = styled(Text, {
  fontSize: '$core.font-size.p.60',
  color: '$core.color.brand.100',
  fontWeight: '$core.font-weight.semibold',
});

const RatingRow = styled('div', {
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$core.spacing.100',
  marginTop: '$core.spacing.25',
});

const StarsText = styled(Text, {
  fontSize: '$core.font-size.p.60',
  letterSpacing: 1,
  color: '$core.color.yellow.100',
});

const RatingText = styled(Text, {
  fontSize: '$core.font-size.p.60',
  color: '$core.color.brand.80',
  fontWeight: '$core.font-weight.xbold',
});

export default ProviderCardDetails;
